import { useEffect, useRef } from 'react'
import { type ModuleKey, switchedOffModuleForView } from '../utils/workspaceModules'
import { isPageHiddenForMe } from '../services/pageVisibilityService'

export interface UseModuleRouteGuardOptions {
  activeLayoutView: string
  switchedOffModules: ReadonlySet<ModuleKey>
  onRedirect: () => void
  onNotify: (key: ModuleKey) => void
  isPageHidden?: (view: string) => boolean
  onNotifyHidden?: () => void
}

/**
 * Route guard: redirect turned-off workspace modules and hidden pages back to DASHBOARD with an info toast.
 */
export function useModuleRouteGuard({
  activeLayoutView,
  switchedOffModules,
  onRedirect,
  onNotify,
  isPageHidden,
  onNotifyHidden,
}: UseModuleRouteGuardOptions): void {
  const onRedirectRef = useRef(onRedirect)
  const onNotifyRef = useRef(onNotify)
  const onNotifyHiddenRef = useRef(onNotifyHidden)

  useEffect(() => {
    onRedirectRef.current = onRedirect
    onNotifyRef.current = onNotify
    onNotifyHiddenRef.current = onNotifyHidden
  })

  useEffect(() => {
    const key = switchedOffModuleForView(activeLayoutView, switchedOffModules)
    if (!key) return
    if (import.meta.env.DEV) {
      console.info('[module-guard] redirect', { view: activeLayoutView, module: key })
    }
    queueMicrotask(() => {
      onRedirectRef.current()
    })
    onNotifyRef.current(key)
  }, [activeLayoutView, switchedOffModules])

  useEffect(() => {
    const checkHidden = isPageHidden ?? isPageHiddenForMe
    if (!checkHidden(activeLayoutView)) return
    if (import.meta.env.DEV) {
      console.info('[module-guard] redirect hidden page', { view: activeLayoutView })
    }
    queueMicrotask(() => {
      onRedirectRef.current()
    })
    onNotifyHiddenRef.current?.()
  }, [activeLayoutView, isPageHidden])
}
